import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuiIconComponent } from '@ral/ui:aui/icons';
import { AuiItemComponent } from '@ral/ui:aui/item';
import { StoryContainerComponent } from '@storyteller/container';
import { StoryDescriptionComponent } from '@storyteller/description';
import { StoryImageComponent, StoryImageSourceComponent } from '@storyteller/image';
import { StoryTitleComponent } from '@storyteller/title';
import { AcpSystemComponent } from '../components/acp-system.component';
import { AntBotCncComponent, EasyLeanKitComponent, MillBarFeederComponent } from '../components';

@Component({
    selector: 'app-about-us-page',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        StoryContainerComponent,
        StoryTitleComponent,
        StoryDescriptionComponent,
        StoryImageComponent,
        StoryImageSourceComponent,
        AuiIconComponent,
        AuiItemComponent,
        RouterLink,
        AcpSystemComponent,
        AntBotCncComponent,
        EasyLeanKitComponent,
        MillBarFeederComponent,
    ],
    template: `
        <article class="bg-info text-surface-background">
            <header>
                <story-container>
                    <div class="m-auto max-w-[800px] text-center">
                        <story-title [headingLevel]="1" [className]="'story-font-display3 pb-6'">
                            Očima zakladatele
                        </story-title>
                        <story-description class="story-gray-text">
                            Příběh, který stojí za značkou Antonyco.
                        </story-description>
                    </div>
                </story-container>
            </header>

            <section>
                <story-container [skipTopPadding]="true" [skipSmallScreenTopPadding]="true">
                    <div class="m-auto max-w-3xl">
                        <div class="px-1 sm:px-4 md:px-14">
                            <p class="mb-6 leading-relaxed">
                                Když jsme s&nbsp;kolegy budovali
                                <a href="https://mkset.cz" target="_blank" class="link">MK SET</a>, žili jsme zakázkovým
                                obráběním od&nbsp;rána do&nbsp;večera. Byly to dny plné kovu, termínů, měření
                                a&nbsp;neustálého přenastavování. Jakmile přišla větší série, scénář byl skoro vždycky
                                stejný: upnout díl do&nbsp;svěráku, spustit, přeměřit, doladit, znovu spustit…
                                a&nbsp;pořád dokola. Poctivá práce, ze&nbsp;které je&nbsp;člověk unavený tak nějak
                                správně, jenže časem jsme začali narážet na&nbsp;limity. Ne&nbsp;těch strojů. Naše.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                Pak přišel moment, kdy jsme už výrobu prostě nestíhali. Nechtěli jsme přidávat odpolední
                                směnu ani nabírat další lidi jen proto, abychom „byli déle v práci“. Chtěli jsme vyrábět
                                chytřeji, ne&nbsp;déle. A&nbsp;protože jsem typ člověka, který si věci nejdřív zkusí
                                představit a&nbsp;až pak koupí, začal jsem si víc všímat videí z&nbsp;automatizovaných
                                pracovišť. Zaujalo mě hlavně to nejjednodušší. Když dokážeš upnout víc dílů najednou,
                                najednou se ti otevře úplně jiný svět. V&nbsp;hlavě mi to cvaklo. Co&nbsp;kdybychom
                                na&nbsp;jeden stůl naložili několik přířezů a&nbsp;nechali stroj pracovat přes noc?
                            </p>

                            <p class="mb-6 leading-relaxed">
                                Začal jsem kreslit, zkoušet a&nbsp;předělávat. Nejdřív to byly nápady, pak prototypy,
                                pak další prototypy, u&nbsp;kterých člověk zjistí, že&nbsp;to&nbsp;vypadá krásně na
                                papíře, ale v&nbsp;reálu to má své mouchy. Až&nbsp;postupně vznikl
                                <a [routerLink]="['/', 'easy-lean-kit']" class="link">Easy Lean Kit</a>. Systém upínek,
                                díky kterému dokážeme obrábět více přířezů současně, bez toho aby u&nbsp;toho musel
                                někdo stát. Večer spustíme stroj a&nbsp;ráno na nás čekají hotové díly připravené na
                                kontrolu. Byl to takový první krok do automatizace. Jednoduchý, praktický a&nbsp;hlavně
                                okamžitě užitečný. Bez robotů, bez velké vědy, jen dobrý nápad převedený do reality.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                Jenže tím to neskončilo. Dařilo se nám držet termíny, zakázky přibývaly a&nbsp;zákazník
                                začal chtít víc. V té době jsme dostali příležitost vyzkoušet kolaborativního robota
                                a&nbsp;zkusit s&nbsp;ním automatické zakládání polotovarů. Upřímně, byl to jeden
                                z&nbsp;těch okamžiků, kdy si člověk říká „aha, tak takhle může vypadat budoucnost“.
                                Výroba se zpřesnila, proces zklidnil a&nbsp;obsluha mohla dělat věci, které mají vyšší
                                hodnotu než jen stát u stroje. A&nbsp;když jsme robota museli vrátit, bylo jasné, že to
                                už nechceme pustit z&nbsp;ruky. Vlastního kobota jsme prostě potřebovali.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                Hledali jsme řešení, které bude spolehlivé, přesné a zároveň přívětivé pro lidi, kteří
                                u&nbsp;CNC skutečně pracují. Nakonec nám jako ideální volba vyšel
                                <a href="https://crx.fanuc.eu/" target="_blank" class="link">Fanuc&nbsp;CRX</a>.
                                K&nbsp;tomu jsme doplnili
                                <a
                                    href="https://schunk.com/cz/cs/uchopovaci-systemy/c/PUB_8293"
                                    target="_blank"
                                    class="link"
                                    >grippery Schunk</a
                                >
                                a&nbsp;rozšíření, které zjednodušuje programování, protože dobrá automatizace není ta,
                                co vypadá skvěle na&nbsp;prezentaci, ale ta, kterou zvládneš používat každý den. Takhle
                                vznikl <a [routerLink]="['/', 'antbot-pro-cnc']" class="link">Antbot pro CNC</a>.
                                Kompletní systém, který v&nbsp;sobě řeší bezpečnost stroje i&nbsp;kobota a&nbsp;přitom
                                se přirozeně napojuje na
                                <a href="https://www.haascnc.com/" target="_blank" class="link">stroje HAAS</a>.
                                Automatické zakládání a&nbsp;odebírání dílů se najednou stalo normální součástí práce.
                                Stroje běžely plynuleji, přesnost byla lepší a&nbsp;prostoje se začaly ztrácet.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                Pak ale přišly první zakázky, které nás postavily před úplně jiný typ otázky. Sériová
                                výroba malých dílů z&nbsp;tyčoviny. Takové ty kusy, které se špatně upínají, jsou malé,
                                vrtkavé, a&nbsp;přitom jich potřebuješ hodně. Chtěli jsme, aby se tyč dala založit
                                jednou a&nbsp;aby si stroj sám postupně odebíral materiál, bez toho, že by u toho někdo
                                pořád stál. Vzpomněl jsem si na&nbsp;prezentaci
                                <a href="https://schunk.com/us/en" target="_blank" class="link">Schunku</a>
                                o&nbsp;jejich LEAN přístupu, kde ovládali gripper přes vnitřní chlazení nástroje.
                                A&nbsp;zase mi v&nbsp;hlavě něco zapadlo. Co kdybychom díl upínali podobně jako na
                                soustruhu, silovým sklíčidlem? Chyběla už jen konstrukce tažné trubky
                                a&nbsp;pneumatického válce.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                S&nbsp;pomocí šikovného kolegy, pár relé a&nbsp;ventilů jsme celý systém nakonec dostali
                                do&nbsp;stavu, kdy se dá ovládat čistě ISO kódem v&nbsp;CNC. Dnes stačí založit tyč
                                a&nbsp;frézka na čtvrté ose jede několik hodin bez obsluhy. Večer ji spustíme
                                a&nbsp;ráno kolegové jen očistí hotové díly. Tak&nbsp;vznikl
                                <a [routerLink]="['/', 'mill-bar-feeder']" class="link">Mill Bar Feeder</a>. Zařízení,
                                které proměnilo běžnou frézku ve stroj schopný automaticky obrábět z&nbsp;tyče
                                i&nbsp;komplexní díly.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                A&nbsp;pak tu zůstala poslední oblast, která se nám pořád vymykala. Kusová výroba. Ta,
                                kde se všechno dělá „po staru“, protože každý díl je jiný, a&nbsp;bez stoprocentní
                                přítomnosti operátora to nejde. Jenže právě tohle je ve výrobě nejdražší. Ne materiál.
                                Ne stroj. Ale čas člověka, který musí být pořád u&nbsp;toho. Automatizovat kusovku, aniž
                                by člověk skončil u&nbsp;nekonečného upínání na paletky, jak to dělá spousta výrobců, se
                                dlouho zdálo skoro nemožné.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                Nakonec nám pomohlo řešení od
                                <a href="https://www.gressel.ch/en/" target="_blank" class="link">Gresselu</a>, od
                                kterého máme většinu svěráků. Před pár lety uvedli upínač s&nbsp;přenosem svěráků
                                a&nbsp;integrovaným momentovým klíčem. Přesně to, co jsme hledali. A&nbsp;zároveň
                                obrovská výzva. Právě ta nás dovedla k&nbsp;nasazení průmyslového robota k&nbsp;pětiosé
                                frézce. Začali jsme stavět vlastní stanici a&nbsp;vyvíjet software, který bude
                                jednoduchý pro operátory i&nbsp;programátory, protože když je systém složitý, lidé ho
                                budou obcházet. A když ho budou obcházet, přestane dávat smysl.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                Tak vznikl <a [routerLink]="['/', 'acp-system']" class="link">ACP System</a>. Do
                                zásobníku můžeme vložit stovky stejných kusů, ale klidně i&nbsp;jeden jediný díl.
                                A&nbsp;zbytek už není starost člověka.
                                <a [routerLink]="['/', 'acp-system']" class="link">ACP System</a> řeší výměnu, přenos,
                                bezpečnost i&nbsp;komunikaci se strojem. Dělá to, co&nbsp;má automatizace dělat. Bere
                                rutinu, bere opakování, bere čekání.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                Na začátku roku 2024 mi došlo, že tohle know-how nemá zůstat jen u&nbsp;nás.
                                Že&nbsp;spousta menších výrobních podniků řeší úplně stejný problém. Mají zakázky, ale
                                nechtějí nebo nemohou nabírat další lidi. Ne&nbsp;proto, že by nechtěli růst, ale
                                protože realita je tvrdá. Najít dobré lidi je těžké, zaučit je ještě těžší a&nbsp;udržet
                                je v dlouhodobé špičce je někdy skoro zázrak. A&nbsp;doba je navíc rozkolísaná. Kdo
                                dokáže pružně reagovat na poptávku, aniž by natahoval směny, má výhodu.
                            </p>

                            <p class="mb-6 leading-relaxed">
                                A&nbsp;tak vznikla značka Antonyco. Ne&nbsp;jako marketingový nápad, ale jako
                                pokračování cesty, kterou jsme si vydřeli vlastními rukama. Naše řešení jsou dostupná,
                                modulární a&nbsp;flexibilní. Umožní začít s&nbsp;automatizací postupně, bez velkých
                                investic a&nbsp;bez toho, aby firma musela překopat celý provoz. A&nbsp;pokud se nám
                                podaří pomoct dalším firmám, aby mohly taky zhasnout, odejít domů a&nbsp;nechat stroje
                                makat za ně, bude to přesně ten důvod, proč tahle cesta dávala smysl.
                            </p>
                            <div class="mt-16 flex flex-col items-center gap-4 sm:gap-8">
                                <story-image
                                    [alt]="'Antonín Solfronk'"
                                    class="block aspect-square w-[100px] overflow-hidden rounded-full"
                                    [fetchPriority]="'low'"
                                >
                                    <story-source
                                        [src]="'assets/images/antonin-solfronk.jpg'"
                                        [width]="100"
                                        [height]="100"
                                    />
                                </story-image>
                                <div class="flex flex-col items-center gap-1">
                                    <p class="font-poppins text-[18px] font-semibold">Ing. Antonín Solfronk</p>
                                    <p class="story-gray-text">Zakladatel Antonyco</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </story-container>
            </section>
        </article>
        <section>
            <div id="solutions" class="relative -top-12 lg:top-[-100px]"></div>
            <story-container>
                <story-title [headingLevel]="2" [className]="'story-font-headline1 pb-12'">Naše řešení</story-title>
                <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10">
                    <div>
                        <app-mill-bar-feeder />
                    </div>

                    <div>
                        <app-easy-lean-kit></app-easy-lean-kit>
                    </div>
                    <div>
                        <app-antbot-pro-cnc></app-antbot-pro-cnc>
                    </div>
                    <div class="lg:col-span-3">
                        <app-acp-system></app-acp-system>
                    </div>
                </div>
            </story-container>
        </section>
    `,
})
export class AboutUsPageComponent {}
